import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import LanguageToggle from '../components/LanguageToggle';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const InstitutionDemoPage = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    institution_name: '',
    contact_person: '',
    email: '',
    phone: '',
    institution_type: 'school',
    student_count: '',
    city: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(
        `${API}/subscriptions/institution/request-demo`,
        {
          ...formData,
          student_count: parseInt(formData.student_count)
        },
        { withCredentials: true }
      );

      setSubmitted(true);
    } catch (error) {
      console.error('Error submitting demo request:', error);
      alert(language === 'tr' 
        ? 'Hata oluştu. Lütfen tekrar deneyin.' 
        : 'Error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
        <LanguageToggle />
        <Navbar title={language === 'tr' ? 'Demo Talebi' : 'Demo Request'} />
        <div className="max-w-2xl mx-auto px-4 py-24 text-center">
          <div className="bg-white rounded-3xl shadow-2xl p-12">
            <div className="text-6xl mb-6">✅</div>
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              {language === 'tr' ? 'Talebiniz Alındı!' : 'Request Received!'}
            </h1>
            <p className="text-xl text-gray-600 mb-8">
              {language === 'tr'
                ? 'Ekibimiz en kısa sürede sizinle iletişime geçecek.'
                : 'Our team will contact you shortly.'}
            </p>
            <button
              onClick={() => navigate('/')}
              className="bg-blue-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-blue-700 transition"
            >
              {language === 'tr' ? 'Ana Sayfaya Dön' : 'Back to Home'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
      <LanguageToggle />
      <Navbar title={language === 'tr' ? 'Kurumsal Demo' : 'Institutional Demo'} />

      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            {language === 'tr' ? '🏫 Kurumsal Demo Talep Formu' : '🏫 Institution Demo Request'}
          </h1>
          <p className="text-xl text-gray-600">
            {language === 'tr'
              ? 'Formu doldurun, size özel bir demo sunumu ve fiyat teklifi hazırlayalım'
              : 'Fill out the form and we\'ll prepare a custom demo and pricing for you'}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              {language === 'tr' ? '📊 Fiyatlandırma' : '📊 Pricing'}
            </h3>
            <div className="space-y-4">
              <div className="bg-blue-50 rounded-xl p-4">
                <p className="text-3xl font-bold text-blue-600 mb-2">₺80</p>
                <p className="text-gray-600">
                  {language === 'tr' ? 'Öğrenci başına/ay' : 'Per student/month'}
                </p>
              </div>
              <p className="text-sm text-gray-600">
                {language === 'tr'
                  ? 'Örnek: 100 öğrenci için ₺8,000/ay'
                  : 'Example: ₺8,000/month for 100 students'}
              </p>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              {language === 'tr' ? '✨ Özellikler' : '✨ Features'}
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span className="text-gray-700 text-sm">
                  {language === 'tr' ? 'Toplu öğrenci ekleme' : 'Bulk student import'}
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span className="text-gray-700 text-sm">
                  {language === 'tr' ? 'Öğrenci karşılaştırma' : 'Student comparison'}
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span className="text-gray-700 text-sm">
                  {language === 'tr' ? 'Detaylı raporlar' : 'Detailed reports'}
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span className="text-gray-700 text-sm">
                  {language === 'tr' ? 'Özel içerik yönetimi' : 'Custom content'}
                </span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2">✓</span>
                <span className="text-gray-700 text-sm">
                  {language === 'tr' ? 'Özel destek' : 'Dedicated support'}
                </span>
              </li>
            </ul>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {language === 'tr' ? 'Kurum Adı *' : 'Institution Name *'}
                </label>
                <input
                  type="text"
                  required
                  value={formData.institution_name}
                  onChange={(e) => setFormData({...formData, institution_name: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {language === 'tr' ? 'Yetkili Kişi *' : 'Contact Person *'}
                </label>
                <input
                  type="text"
                  required
                  value={formData.contact_person}
                  onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {language === 'tr' ? 'E-posta *' : 'Email *'}
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {language === 'tr' ? 'Telefon *' : 'Phone *'}
                </label>
                <input
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {language === 'tr' ? 'Kurum Tipi *' : 'Institution Type *'}
                </label>
                <select
                  required
                  value={formData.institution_type}
                  onChange={(e) => setFormData({...formData, institution_type: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="school">{language === 'tr' ? 'Okul' : 'School'}</option>
                  <option value="tutoring_center">{language === 'tr' ? 'Dersane' : 'Tutoring Center'}</option>
                  <option value="university">{language === 'tr' ? 'Üniversite' : 'University'}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {language === 'tr' ? 'Öğrenci Sayısı *' : 'Student Count *'}
                </label>
                <input
                  type="number"
                  required
                  min="10"
                  value={formData.student_count}
                  onChange={(e) => setFormData({...formData, student_count: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {language === 'tr' ? 'Şehir *' : 'City *'}
                </label>
                <input
                  type="text"
                  required
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {language === 'tr' ? 'Mesajınız' : 'Message'}
                </label>
                <textarea
                  rows="4"
                  value={formData.message}
                  onChange={(e) => setFormData({...formData, message: e.target.value})}
                  placeholder={language === 'tr' 
                    ? 'Özel ihtiyaçlarınız veya sorularınız...'
                    : 'Your specific needs or questions...'}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                ></textarea>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-4 rounded-xl font-bold text-lg hover:shadow-2xl transition-all disabled:opacity-50"
            >
              {loading 
                ? (language === 'tr' ? 'Gönderiliyor...' : 'Submitting...') 
                : (language === 'tr' ? '📞 Demo Talep Et' : '📞 Request Demo')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InstitutionDemoPage;
